import type { StageDef } from '../data/stages.ts';
import { TICK_SECONDS, type IdleRates } from '../model/types.ts';
import type { ResolvedHero } from './build.ts';
import { runCombat } from './combat.ts';

export const TRAVEL_TICKS = 8;

export interface BenchmarkSession extends IdleRates {
  /** Kills per simulated minute. §3.8 lists this beside gold, XP, and drops. */
  killsPerMin: number;
}

/** Five simulated minutes on the farm stage. Same inputs, same rates. */
export function benchmarkSession(args: {
  hero: ResolvedHero;
  stage: StageDef;
  seed: number;
  minutes?: number;
}): BenchmarkSession {
  const minutes = args.minutes ?? 5;
  const totalTicks = Math.round((minutes * 60) / TICK_SECONDS);
  let ticks = 0;
  let gold = 0;
  let xp = 0;
  let dust = 0;
  let dropRolls = 0;
  let kills = 0;
  let fight = 0;
  while (ticks < totalTicks && fight < 400) {
    const result = runCombat({
      hero: args.hero,
      stage: args.stage,
      seed: (args.seed + fight * 997) >>> 0 || 1,
      recordLog: false,
    });
    const spent = Math.max(1, result.ticks) + TRAVEL_TICKS;
    if (ticks + spent > totalTicks && ticks > 0) break;
    ticks += spent;
    gold += result.gold;
    xp += result.xp;
    dust += result.dust;
    dropRolls += result.dropRolls;
    kills += result.kills;
    fight += 1;
    if (result.outcome === 'defeat' && fight > 3 && gold === 0) break;
  }
  const simMinutes = Math.max(ticks * TICK_SECONDS, TICK_SECONDS) / 60;
  return {
    goldPerMin: gold / simMinutes,
    xpPerMin: xp / simMinutes,
    dustPerMin: dust / simMinutes,
    dropRollsPerMin: dropRolls / simMinutes,
    killsPerMin: kills / simMinutes,
  };
}

/** Five simulated minutes on the farm stage. Same inputs, same rates. */
export function benchmarkRates(args: {
  hero: ResolvedHero;
  stage: StageDef;
  seed: number;
  minutes?: number;
}): IdleRates {
  const session = benchmarkSession(args);
  return {
    goldPerMin: session.goldPerMin,
    xpPerMin: session.xpPerMin,
    dustPerMin: session.dustPerMin,
    dropRollsPerMin: session.dropRollsPerMin,
  };
}
